import { Fragment, Suspense } from 'react';
import { createBrowserRouter, Outlet, RouterProvider, useLocation, Navigate } from 'react-router-dom';
import type { ActionFunction, RouteObject, LoaderFunction } from 'react-router-dom';
import MaintenancePage from './pages/Maintenance';
import { setRouteTree } from '@components/ringil3/Features/Assistant/pageContext/pageContextStore';

import { generateModalRoutes, generatePreservedRoutes, generateRegularRoutes } from '@generouted/react-router/core';

type Element = () => JSX.Element;
type Module = {
  default: Element;
  Loader?: LoaderFunction;
  Action?: ActionFunction;
  Catch?: Element;
  Pending?: Element;
};

const PRESERVED = import.meta.glob<Module>('/src/pages/(_app|404).{jsx,tsx}', {
  eager: true,
});
const MODALS = import.meta.glob<Pick<Module, 'default'>>('/src/pages/**/[+]*.{jsx,tsx}', { eager: true });

// Rozdělíme routy na veřejné a soukromé
const PUBLIC_ROUTES = import.meta.glob<Module>(['/src/pages/-[lang]/public/**/*.{jsx,tsx}'], { eager: true });
const PRIVATE_ROUTES = import.meta.glob<Module>(['/src/pages/**/[\\w[-]*.{jsx,tsx,mdx}', '!/src/pages/**/(_!(layout)*(/*)?|_app|404)*', '!/src/pages/-[lang]/public/**/*'], { eager: true });

const preservedRoutes = generatePreservedRoutes<Omit<Module, 'Action'>>(PRESERVED);
const modalRoutes = generateModalRoutes<Element>(MODALS);

const regularRoutes = generateRegularRoutes<RouteObject, Partial<Module>>(PRIVATE_ROUTES, (module, key) => {
  const index = /index\.(jsx|tsx|mdx)$/.test(key) && !key.includes('pages/index') ? { index: true } : {};
  const Default = module?.default || Fragment;
  const Page = () => (module?.Pending ? <Suspense fallback={<module.Pending />} children={<Default />} /> : <Default />);
  return {
    ...index,
    Component: Page,
    ErrorBoundary: module?.Catch,
    loader: module?.Loader,
    action: module?.Action,
  };
});

const publicRoutes = generateRegularRoutes<RouteObject, Partial<Module>>(PUBLIC_ROUTES, (module, key) => {
  const index = /index\.(jsx|tsx|mdx)$/.test(key) && !key.includes('pages/index') ? { index: true } : {};
  const Default = module?.default || Fragment;
  const Page = () => (module?.Pending ? <Suspense fallback={<module.Pending />} children={<Default />} /> : <Default />);
  return {
    ...index,
    Component: Page,
    ErrorBoundary: module?.Catch,
    loader: module?.Loader,
    action: module?.Action,
  };
});

const _app = preservedRoutes?.['_app'];
const _404 = preservedRoutes?.['404'];

const Default = _app?.default || Outlet;

const Modals = () => {
  const Modal = modalRoutes[useLocation().state?.modal] || Fragment;
  return <Modal />;
};

const Layout = () => (
  <>
    <Default /> <Modals />
  </>
);

const App = () => (_app?.Pending ? <Suspense fallback={<_app.Pending />} children={<Layout />} /> : <Layout />);

const app = {
  Component: _app?.default ? App : Layout,
  ErrorBoundary: _app?.Catch,
  loader: _app?.Loader,
};
const fallback = { path: '*', Component: _404?.default || Fragment };

const MaintenanceCheck = () => {
  const isMaintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';
  const location = useLocation();

  if (isMaintenanceMode && location.pathname !== '/maintenance') {
    return <Navigate to="/maintenance" replace />;
  }

  // If maintenance mode is off and the user is on the maintenance page, redirect to the home page
  if (!isMaintenanceMode && location.pathname === '/maintenance') {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

const routes: RouteObject[] = [
  {
    element: <MaintenanceCheck />,
    children: [
      { 
        path: '/maintenance',
        element: <MaintenancePage />
      },
      // Všechny cesty (veřejné i soukromé) pod jazykovou strukturou
      { ...app, children: [...regularRoutes, ...publicRoutes, fallback] }
    ]
  }
];

const router = createBrowserRouter(routes);
// The assistant's page context reports the current route's pattern; handed over from here because
// importing this module from there would be circular.
setRouteTree(router.routes);
export const Routes = () => <RouterProvider router={router} />;
